import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { useI18n } from "@/lib/i18n";
import { FileText, Home as HomeIcon, Info } from "lucide-react";

/**
 * Generic CMS-driven legal page.
 *
 * Handles: /privacy, /terms, /community-rules, /marketplace-rules
 *
 * The Italian source of truth lives in the CMS (`db.pages.<slug>`). When
 * the admin has not yet published a page (or the requested language is
 * missing), the bundled placeholder copy is shown, pointing to
 * `info@toclean.app` — no fake legal content is ever fabricated.
 *
 * The same `response.lang === lang` guard from MissionPage/AboutPage is
 * applied so that publishing a single language in the CMS never turns the
 * whole page monolingual in the other 5 UI languages.
 */
export default function LegalPage({ slug, titleKey }) {
  const { t, lang } = useI18n();
  const [page, setPage] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/pages/${slug}?lang=${encodeURIComponent(lang)}`)
      .then((r) => {
        if (cancel) return;
        const d = r.data || null;
        if (!d || !d.published) { setPage(null); return; }
        const langMatches = !d.lang || d.lang === lang;
        setPage(langMatches ? d : null);
      })
      .catch(() => { if (!cancel) setPage(null); });
    return () => { cancel = true; };
  }, [slug, lang]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`legal-page-${slug}`}>
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <BackButton />
        <div className="inline-flex items-center gap-2 chip bg-emerald-50 text-emerald-800 border border-emerald-200 mt-3 mb-3">
          <FileText size={12} /> {t("legal_kicker")}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight" data-testid={`legal-title-${slug}`}>
          {page?.title || t(titleKey)}
        </h1>
        {page?.body_md ? (
          <div className="mt-5 prose prose-slate max-w-none text-slate-700 whitespace-pre-line text-base leading-relaxed" data-testid={`legal-body-${slug}`}>
            {page.body_md}
          </div>
        ) : (
          <div className="mt-5 bg-amber-50 border border-amber-300 rounded-2xl p-4 sm:p-5 flex gap-3" data-testid={`legal-placeholder-${slug}`}>
            <Info size={18} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-amber-900 text-sm leading-relaxed">
              <div className="font-semibold mb-1">{t("legal_placeholder_title")}</div>
              <div>{t("legal_placeholder_body")} <a className="underline font-medium" href="mailto:info@toclean.app">info@toclean.app</a></div>
            </div>
          </div>
        )}
        <Link to="/" data-testid={`legal-back-${slug}`} className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow">
          <HomeIcon size={14} /> {t("missionpage_back_home")}
        </Link>
      </div>
    </div>
  );
}
